<template>
    <InlineError v-if="form.errors.audio" :message="form.errors.audio"/>

    <div class="mb-12" :class="{ 'md:aspect-w-16 md:aspect-h-6': !progress && form.audio.length == 0 }">
        <div class="flex flex-wrap items-center justify-center border-2 transition-colors duration-100 ease-out border-dashed w-full rounded-xl" :class="{ 'border-regal-blue-700 bg-regal-blue-100 p-6': dragActive, 'border-gray-500 hover:border-gray-600 p-6': !dragActive && !form.audio.length, 'border-transparent': form.audio.length }"
            @dragenter.prevent="toggleDragActive"
            @dragleave.prevent="toggleDragActive"
            @dragover.prevent
            @drop.prevent="drop"
        >

            <div class="flex flex-col w-full space-y-4" v-if="progress || form.audio.length">
                    <AudioPreview v-for="file in form.audio" v-if="previewAvailable" :file="src" :type="type" :progress="progress" :name="file.name" :date="file.lastModifiedDate" :duration="duration"/>
            </div>

            <div v-if="dragActive" class="pointer-events-none w-full flex flex-col space-y-3 items-center justify-center text-regal-blue-700">
                <svg class="w-8 h-8 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13l-3 3m0 0l-3-3m3 3V8m0 13a9 9 0 110-18 9 9 0 010 18z"></path></svg>
                <div class="text-lg font-medium">
                    Drop files here
                </div>
            </div>

            <div v-if="!progress && !form.audio.length && !dragActive" class="text-center space-y-4">
                <div class="w-full flex flex-col items-center justify-center cursor-pointer" @click="$refs.audio.click()">
                    <div class="inline-flex space-x-3 font-bold text-regal-blue-700 border border-regal-blue-700 rounded-full py-3 px-6 hover:bg-regal-blue-700 hover:text-white transition-colors duration-100 ease-in">
                        <div class="">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                        </div>
                        <div class="">
                            Upload audio file
                        </div>
                    </div>
                </div>

                <div v-if="account.plan !== 'Light'" class="font-medium text-gray-700 hidden md:block">
                    — or —
                </div>

                <div class="text-gray-700 space-x-2 w-full items-center justify-center font-medium hidden md:flex">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path></svg>
                    <div class="">
                        Drop files here
                    </div>
                </div>

<!--                 <div v-if="account.plan !== 'Light'" class="w-full flex flex-col items-center justify-center cursor-pointer" @click="open = true">
                    <div class="inline-flex items-center space-x-3 font-bold text-gray-800 border border-gray-600 rounded-full py-3 px-6 mb-2 hover:border-gray-900 hover:text-gray-900 transition-colors duration-100 ease-in">
                        <div class="">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path></svg>
                        </div>
                        <div class="">
                            Record audio
                        </div>
                    </div>
                </div> -->
            </div>
        </div>
    </div>
    <input type="file" accept="audio/*" ref="audio" class="hidden" @input="upload" multiple/>

    <Modal v-model:open="open" @close="confirmIfUnsavedChanges">
        <div class="w-full">
            <RecordAudio @recordingFinished="upload" v-model:isDirty="isDirty" :key="recordAudioKey" :user="$page.props.auth.user"/>
        </div>
    </Modal>

</template>

<script>
import { ref, computed } from 'vue'
import Modal from '@/Shared/Modal'
import AudioPreview from '@/Shared/AudioPreview'
import InlineError from '@/Shared/InlineError'
import RecordAudio from '@/Shared/RecordAudio'
import getBlobDuration from 'get-blob-duration'
import { usePage } from '@inertiajs/inertia-vue3'

export default {
    setup(props) {
        const progress = ref(null)
        const account = computed(() => usePage().props.value.auth.account)
        const dragActive = ref(false)
        const previewAvailable = ref(props.form.audio.length ? true : false)
        const open = ref(false)
        const src = ref(props.form.audio.length ? props.form.audio[0]['previewUrl'] : false)
        const duration = ref(null)
        const isDirty = ref(false)
        const type = ref('audio/mpeg')
        const toggleDragActive = () => {
            dragActive.value = !dragActive.value
        }
        const recordAudioKey = ref(0)

        return {
            progress,
            dragActive,
            toggleDragActive,
            open,
            src,
            previewAvailable,
            duration,
            isDirty,
            recordAudioKey,
            type,
            account
        }
    },
    watch: {
        progress() {
            this.$emit('uploading', this.progress)
        }
    },
    emits: [ 'uploading' ],
    components: { Modal, InlineError, RecordAudio, AudioPreview },
    props: [ 'form', 'supportedAudioFileTypes'],
    methods: {
        confirmIfUnsavedChanges() {
            if (this.isDirty) {
                if (!window.confirm('Do you want to discard unsaved changes?')) {
                    this.open = true
                } else {
                    this.recordAudioKey = this.recordAudioKey + 1
                    this.isDirty = false
                }
            }
        },
        drop(e) {
            this.toggleDragActive()
            this.upload(e.dataTransfer.files)

        },
        validateAudioFiles(file) {
            this.form.errors.audio = null
            if (file.length > 1) {
                return this.form.errors.audio = 'Please select a single file.'
            }
            if (file.length) {
                file = file[0]
            }
            if (!this.supportedAudioFileTypes.includes(file.type)) {
                return this.form.errors.audio = 'Please select a file of the following types: ' + this.supportedAudioFileTypes.join(', ')
            }
            return null
        },
        async getDuration(blob) {
            this.duration = await getBlobDuration(blob)
        },
        upload(e) {
            this.progress = true
            let file = null

            if (e instanceof File) {
                file = e
                this.recordAudioOpen = false
            } else {
                file = !e.target ? e : this.$refs.audio.files[0]
                if (this.validateAudioFiles(file)) {
                    return false
                }
                if (file.length) {
                    file = file[0]
                }
                this.type = file.type
                // const blob = new Blob(file)
            }
            // console.log(file)
            this.src = URL.createObjectURL(file)
            // URL.createObjectURL(new Blob( [ blob ] ) )
            // this.src = URL.createObjectURL(new Blob( [ file ] ))
            // this.getDuration(this.src)

            // let audio = document.createElement('audio')
            // audio.preload = 'metadata'
            // audio.onloadedmetadata = (e) => {
            //     window.URL.revokeObjectURL(audio.src)
            //     // console.log(e.target.duration, e.target)
            //     // this.duration = e.target.duration
            // }
            // audio.src = this.src

            this.previewAvailable = true

            this.form.audio.push({
                name: file.name,
                content_type: file.type,
                extension: file.name.split('.').pop() ?? null,
                lastModifiedDate: file.lastModifiedDate,
            })

            Vapor.store(file, {
                progress: progress => {
                    // this.progress = Math.round(progress * 100)
                    this.progress = true
                }
            }).then(response => {
                this.form.audio[this.form.audio.length - 1] = {
                    name: file.name,
                    content_type: file.type,
                    extension: file.name.split('.').pop() ?? null,
                    lastModifiedDate: file.lastModifiedDate,
                    uuid: response.uuid,
                    key: response.key,
                    bucket: response.bucket,
                    // duration : this.duration
                }
                this.progress = false
                this.open = false
            })
        }
    },
}
</script>
